import { getReadonlyRoute, getStableRouterNavigate } from "@follow/components/atoms/route.js"
import { useMobile } from "@follow/components/hooks/useMobile.js"
import { useSheetContext } from "@follow/components/ui/sheet/context.js"
import type { FeedViewType } from "@follow/constants"
import { getEntry } from "@follow/store/entry/getter"
import { getSubscriptionByFeedId } from "@follow/store/subscription/getter"
import { tracker } from "@follow/tracker"
import { useCallback } from "react"
import { toast } from "sonner"

import { disableShowAISummaryOnce } from "~/atoms/ai-summary"
import { disableShowAITranslationOnce } from "~/atoms/ai-translation"
import { setPreviewBackPath } from "~/atoms/preview"
import { resetShowSourceContent } from "~/atoms/source-content"
import {
  ROUTE_ENTRY_PENDING,
  ROUTE_FEED_IN_FOLDER,
  ROUTE_FEED_IN_INBOX,
  ROUTE_FEED_IN_LIST,
  ROUTE_FEED_PENDING,
} from "~/constants"

import { getTimelineIdByView, useRouteParamsSelector } from "./useRouteParams"

export type NavigateEntryOptions = Partial<{
  timelineId: string
  feedId: string | null
  entryId: string | null
  view: FeedViewType
  folderName: string | null
  inboxId: string
  listId: string
  backPath: string
}>
/**
 * @description a hook to navigate to `feedId`, `entryId`, add search for `view`, `level`
 */
export const useNavigateEntry = () => {
  const sheetContext = useSheetContext()
  const isMobile = useMobile()
  return useCallback(
    (options: NavigateEntryOptions) => {
      navigateEntry(options)
      if (isMobile && sheetContext) {
        sheetContext.dismiss()
      }
    },
    [isMobile, sheetContext],
  )
}

type ParsedNavigateEntryOptions = {
  feedId: string
  timelineId: string
  entryId: string
}

const parseNavigateEntryOptions = (options: NavigateEntryOptions): ParsedNavigateEntryOptions => {
  const { entryId, feedId, view, folderName, inboxId, listId, timelineId } = options || {}
  const route = getReadonlyRoute()
  const { params } = route
  let finalFeedId = feedId || params.feedId || ROUTE_FEED_PENDING
  let finalTimelineId = timelineId || params.timelineId || ROUTE_FEED_PENDING
  const finalEntryId = entryId || ROUTE_ENTRY_PENDING
  const subscription = getSubscriptionByFeedId(finalFeedId)
  const finalView = typeof view === "number" ? view : subscription?.view

  if ("feedId" in options && feedId === null) {
    finalFeedId = ROUTE_FEED_PENDING
  }

  if (folderName) {
    finalFeedId = `${ROUTE_FEED_IN_FOLDER}${folderName}`
  }

  if (listId) {
    finalFeedId = `${ROUTE_FEED_IN_LIST}${listId}`
  }

  if (inboxId) {
    finalFeedId = `${ROUTE_FEED_IN_INBOX}${inboxId}`
  }

  finalFeedId = encodeURIComponent(finalFeedId)

  if (finalView !== undefined && !timelineId) {
    finalTimelineId = getTimelineIdByView(finalView)
  }

  return {
    feedId: finalFeedId,
    timelineId: finalTimelineId,
    entryId: finalEntryId,
  }
}

export function getNavigateEntryPath(options: NavigateEntryOptions | ParsedNavigateEntryOptions) {
  if ("feedId" in options) {
    return `/timeline/${options.timelineId}/${options.feedId}/${options.entryId}`
  }

  const { feedId, timelineId, entryId } = parseNavigateEntryOptions(options)

  return `/timeline/${timelineId}/${feedId}/${entryId}`
}

/*
 * /timeline/:timelineId/:feedId/:entryId
 * timelineId: articles | social-media | view-1 (legacy) | ...
 * feedId: xxx, folder-xxx, list-xxx, inbox-xxx
 * entryId: xxx
 */
export const navigateEntry = (options: NavigateEntryOptions) => {
  const parsedOptions = parseNavigateEntryOptions(options)
  const path = getNavigateEntryPath(parsedOptions)
  const { backPath } = options || {}
  const route = getReadonlyRoute()
  const currentPath = route.location.pathname + route.location.search
  if (path === currentPath) return

  if (backPath) {
    setPreviewBackPath(backPath)
  }

  tracker.navigateEntry({
    feedId: parsedOptions.feedId,
    entryId: parsedOptions.entryId,
    timelineId: parsedOptions.timelineId,
  })

  disableShowAISummaryOnce()
  disableShowAITranslationOnce()
  const sourceContent = getEntry(parsedOptions.entryId)?.settings?.sourceContent
  if (!sourceContent) {
    resetShowSourceContent()
  }

  const navigate = getStableRouterNavigate()

  if (!navigate) {
    const message =
      "Navigation is not available, maybe a mistake in the code, please report an issue. thx."
    toast.error(message)
    throw new Error(message, { cause: "Navigation is not available" })
  }

  return navigate?.(path)
}

export const useBackHome = (timelineId?: string) => {
  const navigate = useNavigateEntry()
  const feedId = useRouteParamsSelector((state) => state.feedId)
  const entryId = useRouteParamsSelector((state) => state.entryId)
  const backToFeed =
    entryId && feedId && entryId !== ROUTE_ENTRY_PENDING && feedId !== ROUTE_FEED_PENDING
  const feedIdToNavigate = backToFeed ? feedId : null

  return useCallback(
    (overvideTimelineId?: string) => {
      navigate({
        feedId: feedIdToNavigate,
        entryId: null,
        timelineId: overvideTimelineId ?? timelineId,
      })
    },
    [navigate, feedIdToNavigate, timelineId],
  )
}
